import type { ChatResponse, ChatRuntime, ChatTurn } from '@its-simple-ai/callcenter-widget';
import { sanitizeRichHtml } from '@/lib/sanitize-html';
import { cambioDeBandaRica, normalizarBandaRica, type BandaRica } from './banda-rica';
import type { Banda } from './bandas';
import { bloquesDelOriginal, mezclaDelFormulario, type BloqueFormulario } from './mezclador';
import { altoMinimoMm, hojaMm, limitesHoja } from './regla';
import {
  ALIGNS, FILL_SCOPES, FONT_FAMILIES, HEADER_MODES, ORIENTATIONS, PAGE_FORMATS, PAGE_NUMBER_FORMATS, PAGE_NUMBER_POSITIONS, TEXT_ALIGNS,
  type BandColumn, type FormatTemplateInput,
} from './types';

/*
 * ASISTENTE DE FORMATO (pedido de Andrey, 13 oct 2026): en el editor de
 * Formatos, el mismo panel del «Asistente de IA» ajusta la plantilla en vivo
 * («márgenes de 2 cm», «un encabezado con una tabla…»). Lo que no es de
 * formato lo sigue contestando el asistente general, como siempre.
 *
 *  - El formulario se registra al montarse (`registrarEditorFormato`).
 *  - `RuntimeConAsistenteFormato` envuelve el runtime del widget: con un
 *    editor registrado, el turno va a POST /api/format-templates/asistente;
 *    sin editor, delega tal cual.
 *  - Lo que devuelve el backend se vuelve a validar aquí (lista blanca,
 *    rangos, candados de la norma, secciones del original, bandas canónicas)
 *    antes de tocar el formulario. Nada se guarda: eso sigue siendo Guardar.
 *  - «deshacer» se resuelve aquí, sin IA, campo a campo.
 *
 * Puro (sin React) para probarlo con vitest.
 */

export type CambiosPlantilla = Partial<FormatTemplateInput>;

export interface TextosAsistente {
  /** Cualquier otro fallo: 5xx (también el 504 del gateway a los 23 s), red, tiempo agotado o respuesta ilegible. */
  error: string;
  deshecho: string;
  nadaQueDeshacer: string;
  parcial: string;
  listo: string;
  /** 429: el cupo de mensajes por persona y minuto, o por cuenta y día. */
  limite: string;
  /** 401: la sesión caducó; reintentar no sirve hasta volver a entrar. */
  sesion: string;
  /** 403: la persona no puede editar plantillas. */
  permiso: string;
}

/** Los fallos que tienen su propio mensaje; el resto es `error`. */
export type MotivoFallo = 'limite' | 'sesion' | 'permiso';
const MOTIVO_POR_ESTADO: Readonly<Record<number, MotivoFallo>> = { 429: 'limite', 401: 'sesion', 403: 'permiso' };

export interface ContextoEditor {
  /** `lockedFields` de la plantilla (norma). */
  bloqueados: readonly string[];
  mixtoActivo: boolean;
  /** Idioma de la interfaz: el backend contesta en él. */
  idioma: string;
  textos: TextosAsistente;
}

export interface EditorFormato {
  obtener: () => FormatTemplateInput;
  /** Reemplaza los valores del formulario (la vista previa se repinta sola). */
  poner: (v: FormatTemplateInput) => void;
  contexto: () => ContextoEditor;
}

export const URL_ASISTENTE = '/api/format-templates/asistente';
export const TIEMPO_MAX_MS = 25_000;
export const MAX_TURNOS = 12;
export const MAX_TEXTO = 2000;
const MAX_RESPUESTA = 1200;
const MAX_DESHACER = 20;

// ---------------------------------------------------------------------------
// Registro del editor montado
// ---------------------------------------------------------------------------

const editores: EditorFormato[] = [];
const oyentes = new Set<() => void>();
const avisar = () => oyentes.forEach((fn) => fn());

interface PasoDeshacer { editor: EditorFormato; antes: Map<string, unknown>; despues: Map<string, unknown> }
let pila: PasoDeshacer[] = [];
const AUSENTE = Symbol('ausente');

/** Devuelve la baja. Al darse de baja, sus pasos de «deshacer» se van con él. */
export function registrarEditorFormato(editor: EditorFormato): () => void {
  editores.push(editor);
  avisar();
  return () => {
    const i = editores.lastIndexOf(editor);
    if (i < 0) return;
    editores.splice(i, 1);
    pila = pila.filter((p) => p.editor !== editor);
    avisar();
  };
}

export const editorFormatoActivo = (): EditorFormato | null => editores[editores.length - 1] ?? null;
export const hayEditorFormato = (): boolean => editores.length > 0;

/**
 * El asistente de formato vive SOLO en el editor de Formatos (pedido de
 * Andrey): /dashboard/format-templates y /admin/format-templates, con o sin
 * el prefijo del idioma. Segunda guarda, por si un registro sobreviviera a su
 * pantalla: fuera de esas rutas el chat es el de siempre.
 */
export const estaEnPantallaDeFormatos = (pathname: string): boolean => /\/format-templates(?:\/|$)/.test(pathname);

const pantallaActual = (): boolean => typeof window !== 'undefined' && estaEnPantallaDeFormatos(window.location.pathname);

export function suscribirEditorFormato(fn: () => void): () => void {
  oyentes.add(fn);
  return () => {
    oyentes.delete(fn);
  };
}

// ---------------------------------------------------------------------------
// Validación de lo que propone el backend
// ---------------------------------------------------------------------------

/** Lista blanca del contrato y la parte del formulario a la que pertenece cada campo (null = ninguna del mixto). */
const SECCION: Record<string, BloqueFormulario | null> = {
  pageFormat: 'hoja', pageOrientation: 'orientacion', unit: null,
  marginTopMm: 'margenes', marginBottomMm: 'margenes', marginLeftMm: 'margenes', marginRightMm: 'margenes',
  headerDistanceMm: 'distancias', footerDistanceMm: 'distancias',
  fontFamily: 'tipografia', fontSizePt: 'tipografia', lineHeight: 'tipografia', textAlign: 'tipografia', paragraphSpaceAfterPt: 'tipografia',
  headerEnabled: 'encabezado', headerAlign: 'encabezado', headerMode: 'encabezado', headerFontSizePt: 'encabezado', headerColumns: 'encabezado',
  footerEnabled: 'pie', footerColumns: 'pie', footerRule: 'pie',
  pageNumbering: null, pageNumberFormat: null, pageNumberStart: null, pageNumberPosition: null,
  fillShortLines: 'guiones', fillChar: 'guiones', fillScope: 'guiones',
  closingFormulaEnabled: 'cierre', closingFormulaHtml: 'cierre', closingKeepTogether: 'cierre', closingLanguage: 'cierre',
  name: null, description: null,
};

export const CAMPOS_ASISTENTE: readonly string[] = Object.keys(SECCION);

const MARGENES = ['marginTopMm', 'marginBottomMm', 'marginLeftMm', 'marginRightMm'] as const;
const DISTANCIAS = ['headerDistanceMm', 'footerDistanceMm'] as const;
const NUMERACION = ['pageNumbering', 'pageNumberFormat', 'pageNumberStart', 'pageNumberPosition'];

const enLista = (lista: readonly string[]) => (x: unknown) => typeof x === 'string' && lista.includes(x);
const numeroEntre = (min: number, max: number) => (x: unknown) => typeof x === 'number' && Number.isFinite(x) && x >= min && x <= max;
const booleano = (x: unknown) => typeof x === 'boolean';

/** Los mismos topes que los campos del formulario. Los márgenes y las distancias se miden luego contra la hoja. */
const VALIDO: Record<string, (x: unknown) => boolean> = {
  pageFormat: enLista(PAGE_FORMATS), pageOrientation: enLista(ORIENTATIONS), unit: enLista(['cm', 'in']),
  marginTopMm: numeroEntre(0, 1000), marginBottomMm: numeroEntre(0, 1000), marginLeftMm: numeroEntre(0, 1000), marginRightMm: numeroEntre(0, 1000),
  headerDistanceMm: numeroEntre(0, 1000), footerDistanceMm: numeroEntre(0, 1000),
  fontFamily: (x) => typeof x === 'string' && FONT_FAMILIES.includes(x),
  fontSizePt: numeroEntre(6, 24), lineHeight: numeroEntre(0.8, 3), textAlign: enLista(TEXT_ALIGNS), paragraphSpaceAfterPt: numeroEntre(0, 48),
  headerEnabled: booleano, headerAlign: enLista(ALIGNS), headerMode: enLista(HEADER_MODES), headerFontSizePt: (x) => x === null || numeroEntre(6, 36)(x),
  footerEnabled: booleano, footerRule: booleano,
  pageNumbering: booleano, pageNumberFormat: enLista(PAGE_NUMBER_FORMATS), pageNumberPosition: enLista(PAGE_NUMBER_POSITIONS),
  pageNumberStart: (x) => Number.isInteger(x) && (x as number) >= 0 && (x as number) <= 9999,
  fillShortLines: booleano, fillChar: (x) => typeof x === 'string' && [...x].length === 1, fillScope: enLista(FILL_SCOPES),
  closingFormulaEnabled: booleano, closingKeepTogether: booleano, closingLanguage: enLista(['es', 'target']),
  closingFormulaHtml: (x) => typeof x === 'string' && x.length <= 20_000,
  name: (x) => typeof x === 'string' && !!x.trim() && x.length <= 120,
  description: (x) => typeof x === 'string' && x.length <= 1000,
};

/** A la décima de mm, como guarda validar() del backend. */
const decimaMm = (mm: number) => Math.round(mm * 10) / 10;

const FIRMA_RE = /<p[^>]*data-ez-firma-propia=[^>]*>[\s\S]*?<\/p>/i;

/** La fórmula de cierre, saneada. La firma o el sello propios (subidos a S3) se conservan aunque el texto nuevo no los traiga. */
function cierreSaneado(nuevo: string, anterior: string | null | undefined): string {
  const limpio = sanitizeRichHtml(nuevo).trim();
  const firma = FIRMA_RE.exec(anterior || '')?.[0];
  return firma && !FIRMA_RE.test(nuevo) ? `${limpio}\n${firma}` : limpio;
}

/** La banda que propone el backend → canónica. [] = vaciarla. Null si no se entiende. */
function bandaPropuesta(valor: unknown): BandaRica | null {
  if (!Array.isArray(valor) || valor.length > 1) return null;
  if (valor.length === 0) return normalizarBandaRica('');
  const c = valor[0] as Partial<BandColumn> | null;
  if (!c || typeof c !== 'object' || c.type !== 'text') return null;
  const align = enLista(ALIGNS)(c.align) ? c.align : 'left';
  const html = typeof c.richHtml === 'string' ? c.richHtml
    : typeof c.text === 'string' ? `<p style="text-align:${align}">${escaparHtml(c.text)}</p>`
    : null;
  return html === null ? null : normalizarBandaRica(html);
}

function geometria(v: FormatTemplateInput) {
  const h = hojaMm(v.pageFormat, v.pageOrientation);
  return limitesHoja({ anchoMm: h.ancho, altoMm: h.alto, altoMinMm: altoMinimoMm(v.fontSizePt, v.lineHeight) });
}

/**
 * Lo que de verdad se aplica: los valores del formulario con los cambios
 * válidos encima, y la lista de los que no pasaron. Nunca toca un campo que
 * la norma bloquea ni una sección que la mezcla toma del original.
 */
export function validarCambios(
  actual: FormatTemplateInput,
  cambios: unknown,
  ctx: Pick<ContextoEditor, 'bloqueados' | 'mixtoActivo'>,
): { nuevo: FormatTemplateInput; rechazados: string[] } {
  const rechazados: string[] = [];
  if (!cambios || typeof cambios !== 'object' || Array.isArray(cambios)) return { nuevo: actual, rechazados };
  const propuestos = cambios as Record<string, unknown>;
  const bloqueado = (k: string) => ctx.bloqueados.includes(k);
  const mezcla = mezclaDelFormulario(actual, ctx.mixtoActivo);
  const delOriginal = bloquesDelOriginal(mezcla);
  // La numeración va en una banda: con las dos del original no tiene dónde ir.
  const numeracionDelOriginal = !!mezcla && mezcla.encabezado === 'original' && mezcla.pie === 'original';
  const libre = (k: string) => {
    const seccion = SECCION[k];
    if (bloqueado(k) || (seccion && delOriginal.has(seccion))) return false;
    return !(numeracionDelOriginal && NUMERACION.includes(k));
  };

  const escalares: Record<string, unknown> = {};
  for (const [k, x] of Object.entries(propuestos)) {
    if (k === 'headerColumns' || k === 'footerColumns') continue;
    if (!(k in SECCION) || !libre(k) || !VALIDO[k]?.(x)) { rechazados.push(k); continue; }
    escalares[k] = (MARGENES as readonly string[]).includes(k) || (DISTANCIAS as readonly string[]).includes(k) ? decimaMm(x as number) : x;
  }
  if (typeof escalares.closingFormulaHtml === 'string') escalares.closingFormulaHtml = cierreSaneado(escalares.closingFormulaHtml, actual.closingFormulaHtml);
  if (typeof escalares.name === 'string') escalares.name = escalares.name.trim();

  let nuevo: FormatTemplateInput = { ...actual, ...escalares };
  // Márgenes que no caben en la hoja: no se guardarían (validar() los rechaza), así que no se aplica ninguno.
  const { maxVertical, maxHorizontal } = geometria(nuevo);
  const tocaMargenes = MARGENES.some((k) => k in escalares);
  if (tocaMargenes && (nuevo.marginTopMm + nuevo.marginBottomMm > maxVertical + 1e-6 || nuevo.marginLeftMm + nuevo.marginRightMm > maxHorizontal + 1e-6)) {
    for (const k of MARGENES) {
      if (!(k in escalares)) continue;
      rechazados.push(k);
      delete escalares[k];
    }
    nuevo = { ...actual, ...escalares };
  }
  for (const k of DISTANCIAS) {
    if (!(k in escalares) || (escalares[k] as number) <= geometria(nuevo).maxVertical + 1e-6) continue;
    rechazados.push(k);
    delete escalares[k];
    nuevo = { ...actual, ...escalares };
  }

  // Encabezado y pie: canónicos y por el mismo camino que el campo del editor (cambioDeBandaRica).
  for (const banda of ['header', 'footer'] as Banda[]) {
    const k = banda === 'header' ? 'headerColumns' : 'footerColumns';
    if (!(k in propuestos)) continue;
    const plano = banda === 'header' ? 'headerText' : 'footerText';
    const r = !bloqueado(k) && !bloqueado(plano) && libre(k) && !actual.mirrorsSource ? bandaPropuesta(propuestos[k]) : null;
    if (!r) { rechazados.push(k); continue; }
    nuevo = { ...nuevo, ...cambioDeBandaRica(nuevo, banda, r, ctx.bloqueados) };
    // Pedir un encabezado (o un pie) con contenido es pedirlo encendido.
    const encendido = banda === 'header' ? 'headerEnabled' : 'footerEnabled';
    if (!r.vacia && !(encendido in escalares) && !nuevo[encendido] && libre(encendido)) nuevo = { ...nuevo, [encendido]: true };
  }
  return { nuevo, rechazados };
}

// ---------------------------------------------------------------------------
// Aplicar y deshacer
// ---------------------------------------------------------------------------

const igual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Aplica al formulario lo que pase la validación y guarda el paso para «deshacer». */
export function aplicarCambios(editor: EditorFormato, cambios: unknown): { cambiados: number; rechazados: string[] } {
  const actual = editor.obtener();
  const { nuevo, rechazados } = validarCambios(actual, cambios, editor.contexto());
  const antes = new Map<string, unknown>();
  const despues = new Map<string, unknown>();
  const viejo = actual as unknown as Record<string, unknown>;
  const fresco = nuevo as unknown as Record<string, unknown>;
  for (const k of new Set([...Object.keys(viejo), ...Object.keys(fresco)])) {
    if (igual(viejo[k], fresco[k])) continue;
    antes.set(k, k in viejo ? viejo[k] : AUSENTE);
    despues.set(k, fresco[k]);
  }
  if (!antes.size) return { cambiados: 0, rechazados };
  pila.push({ editor, antes, despues });
  if (pila.length > MAX_DESHACER) pila = pila.slice(-MAX_DESHACER);
  editor.poner(nuevo);
  return { cambiados: antes.size, rechazados };
}

/**
 * Deshace el último cambio del asistente en ESTE editor. Campo a campo: lo
 * que la persona tocó a mano después se respeta. False si no había nada.
 */
export function deshacerUltimo(editor: EditorFormato): boolean {
  for (let i = pila.length - 1; i >= 0; i--) {
    if (pila[i].editor !== editor) continue;
    const [{ antes, despues }] = pila.splice(i, 1);
    const actual = editor.obtener() as unknown as Record<string, unknown>;
    const restaurado: Record<string, unknown> = { ...actual };
    let n = 0;
    for (const [k, previo] of antes) {
      if (!igual(actual[k], despues.get(k))) continue;
      if (previo === AUSENTE) delete restaurado[k];
      else restaurado[k] = previo;
      n++;
    }
    if (n) editor.poner(restaurado as unknown as FormatTemplateInput);
    return n > 0;
  }
  return false;
}

const PALABRAS_DESHACER = new Set([
  'deshacer', 'deshaz', 'deshazlo', 'undo', 'desfazer', 'desfaz', 'desfaça', 'annuler', 'annule',
  'rückgängig', 'rückgängig machen', 'rueckgaengig',
]);
const CORTESIA = /^(por favor|please|bitte|s'il vous plaît|s’il vous plaît)\s+|\s+(por favor|please|bitte|s'il vous plaît|s’il vous plaît)$/g;

/** «deshacer» (y sus equivalentes) como mensaje entero; una frase que solo lo menciona no cuenta. */
export function esPeticionDeshacer(texto: string): boolean {
  const t = texto.toLowerCase().replace(/[.,;:!¡?¿"'«»“”]/g, ' ').replace(/\s+/g, ' ').trim().replace(CORTESIA, '').trim();
  return PALABRAS_DESHACER.has(t);
}

// ---------------------------------------------------------------------------
// Petición al backend
// ---------------------------------------------------------------------------

export function escaparHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Lo que contestó el asistente general viene en HTML: al backend va como texto. */
export function textoPlanoDeHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s*\n+/g, '\n')
    .trim();
}

export interface MensajeAsistente { rol: 'user' | 'assistant'; texto: string }

/** Los últimos turnos, en texto y con los topes del contrato. */
export function mensajesParaAsistente(history: readonly ChatTurn[]): MensajeAsistente[] {
  return history
    .filter((t) => t.role === 'user' || t.role === 'assistant')
    .map((t) => ({ rol: t.role, texto: (t.role === 'assistant' ? textoPlanoDeHtml(t.content) : t.content.trim()).slice(0, MAX_TEXTO) }))
    .filter((m) => !!m.texto)
    .slice(-MAX_TURNOS);
}

/** La plantilla del formulario sin lo pesado que la IA no necesita (la miniatura y el documento de muestra). */
export function plantillaParaEnviar(v: FormatTemplateInput): Omit<FormatTemplateInput, 'thumbnail' | 'sampleDoc'> {
  const { thumbnail: _t, sampleDoc: _s, ...resto } = v;
  return resto;
}

export type ResultadoAsistente =
  | { tipo: 'ok'; esDeFormato: boolean; respuesta: string; cambios: unknown }
  | { tipo: 'error'; motivo?: MotivoFallo }
  /** El backend no tiene el asistente (404: uno anterior o revertido): el chat sigue como antes. */
  | { tipo: 'sinAsistente' };

/** Error de aborto como el de fetch: el widget lo reconoce cuando corta un turno. */
const abortada = () => new DOMException('The operation was aborted.', 'AbortError');

export async function pedirAsistente(
  cuerpo: unknown,
  fetchImpl: typeof fetch,
  signal: AbortSignal | undefined,
  timeoutMs: number = TIEMPO_MAX_MS,
): Promise<ResultadoAsistente> {
  if (signal?.aborted) throw abortada();
  const control = new AbortController();
  const cortar = () => control.abort();
  signal?.addEventListener('abort', cortar, { once: true });
  const reloj = setTimeout(cortar, timeoutMs);
  try {
    const res = await fetchImpl(URL_ASISTENTE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cuerpo),
      signal: control.signal,
    });
    if (res.status === 404) return { tipo: 'sinAsistente' };
    if (!res.ok) {
      const motivo = MOTIVO_POR_ESTADO[res.status];
      return motivo ? { tipo: 'error', motivo } : { tipo: 'error' };
    }
    const d = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (!d || typeof d.esDeFormato !== 'boolean') return { tipo: 'error' };
    return {
      tipo: 'ok',
      esDeFormato: d.esDeFormato,
      respuesta: typeof d.respuesta === 'string' ? d.respuesta.slice(0, MAX_RESPUESTA) : '',
      cambios: d.cambios,
    };
  } catch {
    if (signal?.aborted) throw abortada();
    return { tipo: 'error' };
  } finally {
    clearTimeout(reloj);
    signal?.removeEventListener('abort', cortar);
  }
}

/** Una respuesta del chat con texto plano: escapado y un párrafo por línea. */
export function respuestaChat(texto: string): ChatResponse {
  const parrafos = texto.split(/\n+/).map((l) => l.trim()).filter(Boolean).map((l) => `<p>${escaparHtml(l)}</p>`);
  return { action: 'answered', content: parrafos.join(''), citations: [], confident: true };
}

/** Saludo del panel con el editor de Formatos abierto, con el mismo formato que el de siempre. */
export const saludoFormatoHtml = (titulo: string, texto: string): string =>
  `<p><strong>${escaparHtml(titulo)}</strong></p><p>${escaparHtml(texto)}</p>`;

/** Lo que pinta el panel antes del primer mensaje: con el editor de Formatos abierto, lo suyo; si no, lo de siempre tal cual. */
export function saludoYSugerencias<S extends { greetingHtml: string; suggestedQuestions: readonly string[] }>(
  conEditorFormato: boolean,
  deSiempre: S,
  tf: { asistSaludoTitulo: string; asistSaludo: string; asistSug1: string; asistSug2: string; asistSug3: string },
): Pick<S, 'greetingHtml' | 'suggestedQuestions'> {
  if (!conEditorFormato) return { greetingHtml: deSiempre.greetingHtml, suggestedQuestions: deSiempre.suggestedQuestions };
  return { greetingHtml: saludoFormatoHtml(tf.asistSaludoTitulo, tf.asistSaludo), suggestedQuestions: [tf.asistSug1, tf.asistSug2, tf.asistSug3] };
}

/** Los textos del asistente, de `t.formatTemplates`. */
export const textosAsistente = (tf: {
  asistError: string; asistDeshecho: string; asistNadaQueDeshacer: string; asistParcial: string; asistListo: string;
  asistLimite: string; asistSesion: string; asistPermiso: string;
}): TextosAsistente => ({
  error: tf.asistError, deshecho: tf.asistDeshecho, nadaQueDeshacer: tf.asistNadaQueDeshacer, parcial: tf.asistParcial, listo: tf.asistListo,
  limite: tf.asistLimite, sesion: tf.asistSesion, permiso: tf.asistPermiso,
});

// ---------------------------------------------------------------------------
// Runtime del widget
// ---------------------------------------------------------------------------

/**
 * Envuelve el runtime del widget. Sin editor de Formatos registrado, o fuera
 * de su pantalla, delega tal cual. Con editor, el turno va al asistente de
 * formato; si no es de formato, sigue al runtime de siempre. Un turno con
 * adjuntos va siempre al de siempre (el asistente de formato solo lee texto).
 */
export class RuntimeConAsistenteFormato implements ChatRuntime {
  constructor(
    private readonly base: ChatRuntime,
    private readonly opts: { fetch?: typeof fetch; timeoutMs?: number; enPantallaDeFormatos?: () => boolean } = {},
  ) {}

  async sendTurn(history: ChatTurn[], signal?: AbortSignal): Promise<ChatResponse> {
    const editor = editorFormatoActivo();
    const ultimo = history[history.length - 1];
    const enFormatos = (this.opts.enPantallaDeFormatos ?? pantallaActual)();
    if (!editor || !enFormatos || ultimo?.role !== 'user' || !ultimo.content.trim() || (ultimo.attachments?.length ?? 0) > 0) {
      return this.base.sendTurn(history, signal);
    }
    const { textos, bloqueados, idioma } = editor.contexto();
    if (esPeticionDeshacer(ultimo.content)) return respuestaChat(deshacerUltimo(editor) ? textos.deshecho : textos.nadaQueDeshacer);

    const cuerpo = {
      mensajes: mensajesParaAsistente(history),
      plantilla: plantillaParaEnviar(editor.obtener()),
      bloqueados: [...bloqueados],
      idioma,
    };
    const fetchImpl = this.opts.fetch ?? globalThis.fetch.bind(globalThis);
    const r = await pedirAsistente(cuerpo, fetchImpl, signal, this.opts.timeoutMs);
    if (r.tipo === 'error') return respuestaChat(r.motivo ? textos[r.motivo] : textos.error);
    if (r.tipo === 'sinAsistente' || !r.esDeFormato) return this.base.sendTurn(history, signal);
    // El editor pudo cerrarse mientras contestaba la IA: no se toca otro formulario.
    if (editorFormatoActivo() !== editor) return respuestaChat(textos.error);
    const { rechazados } = aplicarCambios(editor, r.cambios);
    const texto = r.respuesta.trim() || textos.listo;
    return respuestaChat(rechazados.length ? `${texto}\n${textos.parcial}` : texto);
  }
}
